import { useEffect, useRef, useState } from "react";
import {
  useFloating,
  useInteractions,
  useTransitionStyles,
  useDismiss,
  flip,
  shift,
  useClick,
  useHover,
  offset,
  arrow,
  autoUpdate
} from "@floating-ui/react";

import { PopoverContainerProps } from "./Popover.types";

import Popover from "./Popover";

//FIXME: add typings
const PopoverContainer = ({
  dismissable = true,
  placement,
  triggerType = "click",
  mode = "uncontrolled",
  yOffset = 0,
  xOffset = 0,
  triggerOpen,
  placementMiddleware = false,
  floatingArrowProps,
  ...restProps
}: PopoverContainerProps) => {

  const [open, setOpen] = useState(false);

  const arrowRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (mode === "controlled" && triggerOpen) {
      setOpen((prevOpen) => !prevOpen);
    }
  }, [mode, triggerOpen]);

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement || "right-end",
    middleware: placementMiddleware
      ? [
        flip(),
        shift(),
        arrow({ element: arrowRef, }),
        offset(
          ({ rects }) =>
            -rects.reference.height / 2 - rects.floating.height / 2
        ),
      ]
      : [flip(), shift(), arrow({ element: arrowRef, }),],
    whileElementsMounted: autoUpdate,
  });

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "translateY(-10px)",
    },
    open: { opacity: 1, transform: "translateY(0)" },
  });

  const click = useClick(context, { enabled: triggerType === "click" });
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
    enabled: dismissable,
  });
  const hover = useHover(context, { enabled: triggerType === "hover" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
  ]);

  const handlePopoverMouseDown = () => {
    setOpen(false);
  };

  const styleProps = {
    position: strategy,
    top: y + ((/top/.test(context.placement) ? -(yOffset) : yOffset) || 0) || 0,
    left: x + ((/left|end/.test(context.placement) ? -(xOffset) : xOffset) || 0) || 0,
  };

  const styleArrowProps = {
    transform: /top|bottom/.test(placement || "") ? `translateX(${((/left|end/.test(context.placement) ? -(xOffset) : xOffset) || 0)}px)` : `translateY(${((/top/.test(context.placement) ? -(yOffset) : yOffset) || 0)}px)`,
  }

  return (
    <Popover
      {...restProps}
      isMounted={isMounted}
      refs={refs}
      animationStyles={styles}
      styleProps={styleProps}
      placement={placement}
      mode={mode}
      getReferenceProps={getReferenceProps}
      getFloatingProps={getFloatingProps}
      onPopoverMouseDown={handlePopoverMouseDown}
      arrowRef={arrowRef}
      context={context}
      floatingArrowProps={floatingArrowProps}
      styleArrowProps={styleArrowProps}
    />
  );
};

export default PopoverContainer;
